import { useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useParams, useSearchParams } from 'react-router';
import { lookup } from '@stitch/pbx-adapter/describe';
import { formatMac, keyName, keyText, keyType, lineKeyCount, parseKeys, phoneModel } from '@stitch/pbx-adapter/phones';
import { api, callsApi, type CallRecord, type PersonPresence, type SessionHistoryItem } from '../../api/client';
import { PhoneLayout, whereIs } from '../../components/PhoneLayout';
import { useTenant } from '../../components/Shell';
import { Button, Card, Chip, Empty, LoadError, Loading, Notice, PageHeader } from '../../components/ui';
import { useAuth } from '../../lib/auth';
import { fullWhen, plural, shownNumber } from '../../lib/format';
import { timesIn } from '../../lib/time';
import { Icon } from '../../lib/icons';
import { useData } from '../../lib/useData';
import { duration } from './Calls';
import { VoicemailBox } from '../../components/Voicemail';
import { VoicemailGreetingCard } from '../../components/VoicemailGreeting';
import { modelLabel, namer } from './Phones';
import { PatchHistory } from '../../components/PatchHistory';
import { DialPad } from '../../components/CallDock';
import { useCalls } from '../../lib/calls';
import { useLiveSessions } from '../../lib/sessions';
import { namesText } from '../../lib/patchText';
import { PhonePageHead, usePhone } from '../../components/PhoneApp';
import { presenceText } from '../../components/Presence';
import { useMessagesLive } from '../../lib/messages';
import type { Directory } from '@stitch/pbx-adapter';

/**
 * A person's own phone, in three parts: right now (their line, do not disturb, their call queues,
 * day and night), voicemail and calls, and their desk phone (click a key to see what it does).
 * Do not disturb and calls follow the phone system as they change. Admins see anyone's at
 * /view/:extension.
 */
export function MyPhone() {
  const { tenant } = useTenant();
  const d = tenant.domain;
  const { extension: viewing } = useParams();
  const { state } = useAuth();
  const me = useData(() => api.me(d, viewing), [d, viewing]);
  const dir = useData(() => api.directory(d).catch(() => undefined), [d]);
  const calls = useData(() => api.myCalls(d, viewing).catch(() => null), [d, viewing], { topics: ['calls'] });
  const [phoneIx, setPhoneIx] = useState(0);
  const [selected, setSelected] = useState<string>();
  const [busy, setBusy] = useState('');
  const [problem, setProblem] = useState('');
  const base = `/t/${encodeURIComponent(d)}`;
  const isMe = !viewing;
  // Their line right now. Changes arrive as they happen: do not disturb switched in another app of
  // theirs or on the desk phone, a call ringing. It's read again every 20 seconds for what isn't
  // told (who with, phones signed in), when the page comes back into view, and when the live stream
  // reconnects (an app back from the background may have missed something).
  const line = useData(() => api.myLine(d, viewing).catch(() => null), [d, viewing]);
  const [heard, setHeard] = useState<{ extension: string; state: PersonPresence['state']; dnd?: boolean; since?: string }>();
  const member = Boolean(state?.memberships?.some((m) => m.tenant === d.toLowerCase()));
  // Calls in the app (the dial pad), when they're on for this company.
  const calling = useCalls();
  const mine = me.data?.extension;
  useMessagesLive((e) => {
    if (e.type === 'reset' || e.type === 'hello') {
      line.reload();
      return;
    }
    if (e.type !== 'line' || e.tenant !== d.toLowerCase() || !e.extension || !e.state || e.extension !== mine) return;
    setHeard({ extension: e.extension, state: e.state, ...(e.dnd !== undefined ? { dnd: e.dnd } : {}), ...(e.since ? { since: e.since } : {}) });
    line.reload();
  }, member);
  // A fresh read replaces what was heard (it already has it).
  useEffect(() => setHeard(undefined), [line.data]);
  const reloadLine = line.reload;
  useEffect(() => {
    const t = window.setInterval(() => document.visibilityState === 'visible' && reloadLine(), 20_000);
    const back = () => document.visibilityState === 'visible' && reloadLine();
    document.addEventListener('visibilitychange', back);
    return () => {
      window.clearInterval(t);
      document.removeEventListener('visibilitychange', back);
    };
  }, [reloadLine]);
  // From a voicemail email or notification (…/me#voicemail): to the voicemail, once it's on screen.
  const { hash } = useLocation();
  const [search] = useSearchParams();
  const phoneSize = usePhone();
  const ready = Boolean(me.data);
  useEffect(() => {
    if (hash !== '#voicemail' || !ready) return;
    const t = window.setTimeout(() => document.getElementById('voicemail')?.scrollIntoView({ block: 'start' }), 50);
    return () => window.clearTimeout(t);
  }, [hash, ready]);

  // On a phone, a new voicemail's notification opens Calls → Voicemail.
  if (phoneSize && !viewing && search.get('voicemail')) return <Navigate to={`${base}/me/calls?show=voicemail`} replace />;
  if (me.error) {
    return (
      <div className="content">
        <Card>
          <LoadError error={me.error} retry={me.reload} />
        </Card>
      </div>
    );
  }
  if (!me.data) return <Loading label="Loading your phone" />;
  const v = me.data;
  const phone = v.phones[phoneIx] ?? v.phones[0];
  const model = phone ? phoneModel(phone.values.model ?? '') : undefined;
  const keys = parseKeys(phone?.values.keys ?? '');
  const nameOf = namer(dir.data);
  const sel = keys.find((k) => k.key === selected);
  const first = v.name.split(' ')[0] || v.name;
  const h = heard?.extension === v.extension ? heard : undefined;
  const read = line.data?.line ?? null;
  const now: PersonPresence | null = read
    ? { ...read, ...(h ? { state: h.state, ...(h.dnd !== undefined ? { dnd: h.dnd } : {}), ...(h.since ? { since: h.since } : {}) } : {}) }
    : h
      ? { state: h.state, dnd: h.dnd ?? Boolean(v.dnd), phones: 0, ...(h.since ? { since: h.since } : {}) }
      : null;
  // What was just heard, else the line as read (it follows Stitch's own switches and, with the live
  // connection, the phone's), else the extension's setting.
  const dndNow = h?.dnd ?? read?.dnd ?? v.dnd;

  async function setDnd(on: boolean) {
    setBusy('dnd');
    setProblem('');
    try {
      const r = await api.myDnd(d, on, viewing);
      if (r.status !== 'applied' && r.status !== 'unchanged') setProblem(r.message ?? 'That didn’t work. Try again.');
      else setHeard({ extension: v.extension, state: now?.state ?? 'idle', dnd: on });
      me.reload();
      line.reload();
    } catch (e) {
      setProblem(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy('');
    }
  }

  async function toggle(queue: string, loggedIn: boolean) {
    setBusy(queue);
    setProblem('');
    try {
      const r = await api.myQueue(d, queue, loggedIn, viewing);
      if (r.status !== 'applied' && r.status !== 'unchanged') setProblem(r.message ?? 'That didn’t work. Try again.');
      me.reload();
    } catch (e) {
      setProblem(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy('');
    }
  }

  return (
    <>
      <PageHeader
        eyebrow={
          isMe ? (
            `Extension ${v.extension}`
          ) : (
            <>
              <Link to={`${base}/people`}>People</Link> · What {first} sees
            </>
          )
        }
        title={isMe ? `Hi ${first}` : v.name}
        actions={
          !isMe && state?.user ? (
            <Chip tone="brand">
              <Icon name="eye" size={14} /> Viewing as {first}
            </Chip>
          ) : undefined
        }
      />
      <div className="content my-page">
        {problem && <Notice tone="crit">{problem}</Notice>}

        {/* Right now: how calls reach them. Do not disturb and calls follow the phone system live. */}
        <section className="my-section" aria-labelledby="my-now-title">
          <h2 id="my-now-title" className="my-section-title">
            {isMe ? 'Right now' : `${first} right now`}
          </h2>
          <div className="my-now">
            {isMe && member && calling.on && (
              <Card title="Make a call">
                <div className="card-body">
                  <DialPad />
                </div>
              </Card>
            )}
            <LineCard line={now} dnd={dndNow} isMe={isMe} dir={dir.data} loading={line.loading && !line.data} />
            {v.dnd !== undefined && (
              <Card title="Do not disturb">
                <div className="card-body dnd-row">
                  <span className="cell-stack grow">
                    <strong>{dndNow ? 'On: calls don’t ring' : 'Off'}</strong>
                    <small className="muted">{dndNow ? 'Callers go to voicemail, or wherever unanswered calls go.' : `Calls ring ${isMe ? 'your' : 'their'} phones as usual.`}</small>
                  </span>
                  {v.canDnd ? (
                    <button
                      type="button"
                      role="switch"
                      aria-checked={Boolean(dndNow)}
                      aria-label="Do not disturb"
                      className={`toggle${dndNow ? ' on' : ''}`}
                      disabled={busy === 'dnd'}
                      onClick={() => void setDnd(!dndNow)}
                    >
                      <span />
                    </button>
                  ) : (
                    <Chip small tone={dndNow ? 'warn' : 'neutral'}>{dndNow ? 'On' : 'Off'}</Chip>
                  )}
                </div>
              </Card>
            )}
            <Card title={isMe ? 'Your call queues' : 'Call queues'}>
              <div className="card-body">
                {v.queues === null ? (
                  <p className="muted">Couldn’t be read just now.</p>
                ) : v.queues.length === 0 ? (
                  <p className="muted">{isMe ? 'You aren’t in any call queues.' : 'Not in any call queues.'}</p>
                ) : (
                  <ul className="my-list">
                    {v.queues.map((q) => (
                      <li key={q.extension}>
                        <span className="cell-stack">
                          <strong>{q.name}</strong>
                          <small className="muted">
                            Queue {q.extension}
                            {q.manager ? ' · manager' : ''}
                          </small>
                        </span>
                        {q.agent ? (
                          v.canSignIn ? (
                            <Button size="small" variant={q.loggedIn ? 'secondary' : 'primary'} disabled={busy === q.extension} onClick={() => void toggle(q.extension, !q.loggedIn)}>
                              {busy === q.extension ? 'One moment…' : q.loggedIn ? 'Sign out' : 'Sign in'}
                            </Button>
                          ) : (
                            <Chip small tone={q.loggedIn ? 'ok' : 'neutral'}>
                              {q.loggedIn ? 'Signed in' : 'Signed out'}
                            </Chip>
                          )
                        ) : (
                          <Chip small>Watching</Chip>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                {/* Messages' "Sign in" links lead here (2984): when Stitch can't sign people in, say how. */}
                {isMe && !v.canSignIn && v.queues?.some((q) => q.agent && !q.loggedIn) && (
                  <p className="muted small-print">Stitch can’t sign you in to queues here right now. Sign in on your desk phone, or ask your admin.</p>
                )}
              </div>
            </Card>
            {v.switches && v.switches.length > 0 && (
              <Card title="Day and night">
                <ul className="my-list card-body">
                  {v.switches.map((sw) => (
                    <li key={sw.extension}>
                      <span className="cell-stack">
                        <strong>{sw.name}</strong>
                        <small className="muted">{sw.on ? 'On now' : 'Off now'}</small>
                      </span>
                      <Chip small tone={sw.on ? 'warn' : 'neutral'}>
                        {sw.on ? 'On' : 'Off'}
                      </Chip>
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </div>
        </section>

        {/* Voicemail and calls. */}
        <section className="my-section" aria-labelledby="my-calls-title">
          <h2 id="my-calls-title" className="my-section-title">
            Voicemail and calls
          </h2>
          <Card title={isMe ? 'Your voicemail' : 'Voicemail'} id="voicemail">
            <div className="card-body">
              <VoicemailBox domain={d} extension={viewing} dir={dir.data} own={isMe} />
            </div>
          </Card>
          {/* What callers hear before the beep: typed here, spoken by the phone system (1.12.33). */}
          <VoicemailGreetingCard domain={d} extension={viewing} own={isMe} />
          <Card
            title="Recent calls"
            action={
              isMe ? (
                <Link to={`${base}/me/calls`} className="link-btn">
                  All my calls
                </Link>
              ) : undefined
            }
          >
            <div className="card-body">
              <CallList calls={calls.data?.calls.slice(0, 8)} loading={calls.loading} me={v.extension} dirName={(n) => lookup(dir.data, n).name} />
            </div>
          </Card>
          <RecordedCalls domain={d} extension={viewing} dirName={(n) => lookup(dir.data, n).name} me={v.extension} />
        </section>

        {/* The desk phone, last: what each key does. */}
        <section className="my-section" aria-labelledby="my-desk-title">
          <h2 id="my-desk-title" className="my-section-title">
            {isMe ? 'Your desk phone' : 'Desk phone'}
          </h2>
          <Card
            title={phone ? modelLabel(phone.values.model) : 'Desk phone'}
            action={
              v.phones.length > 1 ? (
                <div className="segmented" role="radiogroup" aria-label="Phone">
                  {v.phones.map((ph, i) => (
                    <label key={ph.extension} className={i === phoneIx ? 'on' : ''}>
                      <input type="radio" name="my-phone" checked={i === phoneIx} onChange={() => (setPhoneIx(i), setSelected(undefined))} />
                      {modelLabel(ph.values.model)}
                    </label>
                  ))}
                </div>
              ) : undefined
            }
          >
            <div className="card-body">
              {!phone ? (
                <Empty title="No desk phone yet">
                  {isMe ? 'Use the app on your computer or mobile for now. Ask your admin if you should have a desk phone.' : 'They don’t have a desk phone.'}
                </Empty>
              ) : !model ? (
                <p className="muted">
                  {modelLabel(phone.values.model)} · <span className="mono">{formatMac(phone.extension)}</span>
                </p>
              ) : (
                <div className="my-phone">
                  <PhoneLayout
                    model={model}
                    keys={keys}
                    nameOf={nameOf}
                    selected={selected}
                    onSelect={(k) => setSelected((x) => (x === k ? undefined : k))}
                    modules={keys.reduce((m, k) => Math.max(m, Number(/^E(\d)-/.exec(k.key)?.[1] ?? 0)), 0)}
                    owner={`${v.extension}  ${v.name}`}
                    lineKeys={lineKeyCount(model, keys, phone.live?.phone?.slots)}
                  />
                  <div className="my-key" aria-live="polite">
                    {selected ? (
                      <>
                        <strong>{keyName(selected)}</strong>
                        <span className="muted">{whereIs(model, selected, lineKeyCount(model, keys, phone.live?.phone?.slots))}</span>
                        {sel ? (
                          <p>
                            <strong>{keyText(sel, nameOf)}</strong>: {sel.type === 'other' ? 'set up in the phone system.' : (keyType(sel.type)?.description ?? '')}
                            {sel.target && keyType(sel.type)?.target && !nameOf(sel.target) && <> ({sel.target})</>}
                          </p>
                        ) : (
                          <p className="muted">Does what the phone does by default.</p>
                        )}
                      </>
                    ) : (
                      <p className="muted">
                        {phone.live?.phone?.online ? (
                          <Chip small tone="ok">
                            Online
                          </Chip>
                        ) : (
                          <Chip small>Offline</Chip>
                        )}{' '}
                        {modelLabel(phone.values.model)}. Tap a key to see what it does.
                        {isMe ? ' To change your keys, ask your admin.' : ''}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </Card>
        </section>
      </div>
    </>
  );
}

/** Their line right now: on a call (with whom, for how long), ringing, do not disturb, available. */
function LineCard({ line, dnd, isMe, dir, loading }: { line: PersonPresence | null; dnd: boolean | undefined; isMe: boolean; dir: Directory | undefined; loading: boolean }) {
  // A call's time counts up while it's shown.
  const [, tick] = useState(0);
  const talking = line?.state === 'talking';
  useEffect(() => {
    if (!talking) return;
    const t = window.setInterval(() => tick((n) => n + 1), 1000);
    return () => window.clearInterval(t);
  }, [talking]);
  const t = line ? presenceText({ ...line, dnd: dnd ?? line.dnd }, dir) : null;
  return (
    <Card title={isMe ? 'Your line' : 'Their line'}>
      <div className="card-body my-line" aria-live="polite">
        {t && line ? (
          <>
            <span className={`presence my-line-state ${t.tone}`}>
              <span className="presence-dot" aria-hidden="true" />
              <strong>{t.label}</strong>
              {t.detail && <span className="muted mono"> · {t.detail}</span>}
            </span>
            <small className="muted">
              {line.phones === 0
                ? `No phone or app is signed in on ${isMe ? 'your' : 'their'} extension, so calls can’t ring.`
                : `${plural(line.phones, 'phone or app', 'phones or apps')} signed in.`}
            </small>
          </>
        ) : (
          <small className="muted">{loading ? 'Looking…' : 'Couldn’t be read just now.'}</small>
        )}
      </div>
    </Card>
  );
}

/** Calls of theirs the phone system recorded, to play back here. */
function RecordedCalls({ domain, extension, dirName, me }: { domain: string; extension?: string; dirName: (n: string) => string; me: string }) {
  // Ten at first; "Show more" loads further pages of the phone system's list.
  const [pages, setPages] = useState(1);
  const [shown, setShown] = useState(10);
  const recs = useData(async () => {
    const all = await Promise.all(Array.from({ length: pages }, (_, i) => api.myRecordings(domain, extension, i + 1)));
    return {
      recordings: all.flatMap((p) => p.recordings),
      more: Boolean(all[all.length - 1]?.more)
    };
  }, [domain, extension, pages]);
  const [playing, setPlaying] = useState('');
  const list = recs.data?.recordings ?? [];
  if (!recs.data || list.length === 0) return null;
  const more = shown < list.length || recs.data.more;
  const showMore = () => {
    if (shown + 25 > list.length && recs.data?.more) setPages((n) => n + 1);
    setShown((n) => n + 25);
  };
  return (
    <Card title="Recorded calls">
      <ul className="vm-list card-body">
        {list.slice(0, shown).map((r) => {
          const other = r.from.number === me ? r.to : r.from;
          const name = other.name || dirName(other.number) || other.number || 'Unknown';
          return (
            <li key={r.id}>
              <div className="vm-row">
                <button type="button" className="icon-btn vm-play" aria-label={`Play the call with ${name}`} onClick={() => setPlaying(r.id)}>
                  <Icon name="play" size={16} />
                </button>
                <span className="cell-stack grow">
                  <strong>{name}</strong>
                  <small className="muted">
                    {fullWhen(r.time)} · {duration(r.seconds)}
                  </small>
                </span>
              </div>
              {playing === r.id && <audio className="vm-audio" controls autoPlay src={api.myRecordingUrl(domain, r.id, extension)} aria-label={`Call with ${name}`} />}
            </li>
          );
        })}
      </ul>
      {more && (
        <div className="vm-more">
          <Button variant="secondary" size="small" disabled={recs.loading} onClick={showMore}>
            {recs.loading ? 'Loading…' : 'Show more'}
          </Button>
        </div>
      )}
    </Card>
  );
}

type HistoryShow = 'all' | 'calls' | 'patches' | 'meetings';

/**
 * Calls and Patches in one list, newest first (Live sessions, 1.13.0, Teampace 3023), with a
 * filter by kind: the phone system's calls, and Patches you were in or missed.
 */
function SessionHistory({ domain, calls, loading, me, dirName }: { domain: string; calls: CallRecord[] | undefined; loading: boolean; me: string; dirName: (n: string) => string }) {
  const [show, setShow] = useState<HistoryShow>('all');
  const patches = useData(() => callsApi.sessions(domain).catch(() => ({ sessions: [] as SessionHistoryItem[] })), [domain], { topics: ['calls'] });
  const list = patches.data?.sessions ?? [];
  const items = [
    ...(show === 'all' || show === 'calls' ? (calls ?? []).map((c) => ({ at: c.start, call: c as CallRecord | null, patch: null as SessionHistoryItem | null })) : []),
    ...list
      .filter((p) => show === 'all' || (show === 'patches' && p.kind === 'patch') || (show === 'meetings' && p.kind === 'meeting'))
      .map((p) => ({ at: p.startedAt, call: null as CallRecord | null, patch: p as SessionHistoryItem | null }))
  ].sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
  const chip = (v: HistoryShow, label: string) => (
    <button type="button" aria-pressed={show === v} onClick={() => setShow(v)}>
      {label}
    </button>
  );
  return (
    <>
      <div className="filters history-kinds" role="group" aria-label="Show">
        {chip('all', 'All')}
        {chip('calls', 'Calls')}
        {chip('patches', 'Patches')}
        {chip('meetings', 'Meetings')}
      </div>
      {!calls && (show === 'all' || show === 'calls') && loading ? (
        <Loading label="Loading calls" />
      ) : !items.length ? (
        <Empty title={show === 'patches' ? 'No Patches yet' : show === 'meetings' ? 'No meetings yet' : 'No calls this week'} />
      ) : (
        <ul className="call-list">
          {items.map((x) =>
            x.call ? (
              <CallRow key={`c:${x.call.id}`} c={x.call} me={me} dirName={dirName} />
            ) : x.patch!.kind === 'meeting' ? (
              <li key={`m:${x.patch!.id}`}>
                <Icon name="video" size={16} />
                <span className="cell-stack grow">
                  <Link to={`/t/${encodeURIComponent(domain)}/meetings/${encodeURIComponent(x.patch!.meetingId ?? '')}`}>
                    <strong>{x.patch!.title ?? 'Meeting'}</strong>
                  </Link>
                  <small className="muted">
                    {x.patch!.with.length ? `With ${namesText(x.patch!.with)} · ` : ''}
                    {fullWhen(x.patch!.startedAt)}
                  </small>
                </span>
                <span className="muted mono">{duration(x.patch!.seconds)}</span>
              </li>
            ) : (
              <li key={`p:${x.patch!.id}`} className={x.patch!.missed ? 'missed' : ''}>
                <Icon name="headset" size={16} />
                <span className="cell-stack grow">
                  <Link to={`/t/${encodeURIComponent(domain)}/messages/${encodeURIComponent(x.patch!.conversationId ?? '')}`}>
                    {/* The time is in its own column, as for calls. */}
                    <strong>{x.patch!.missed ? x.patch!.text : x.patch!.with.length ? `Patch with ${namesText(x.patch!.with)}` : 'Patch'}</strong>
                  </Link>
                  <small className="muted">{fullWhen(x.patch!.startedAt)}</small>
                </span>
                <span className="muted mono">{x.patch!.missed ? 'Missed' : duration(x.patch!.seconds)}</span>
              </li>
            )
          )}
        </ul>
      )}
    </>
  );
}

function CallRow({ c, me, dirName }: { c: CallRecord; me: string; dirName: (n: string) => string }) {
  const mine = (p: CallRecord['from']) => p.number === me;
  const other = mine(c.from) ? c.to : c.from;
  const inbound = !mine(c.from);
  const name = c.patch?.text || other.name || (other.number ? dirName(other.number) : '') || other.number || 'Unknown';
  return (
    <li className={c.missed ? 'missed' : ''}>
      <Icon name={inbound ? 'arrowIn' : 'arrowOut'} size={16} />
      <span className="cell-stack grow">
        <strong>{name}</strong>
        <small className="muted">
          {other.number && shownNumber(other.number) !== name ? `${shownNumber(other.number)} · ` : ''}
          {fullWhen(c.start)}
        </small>
      </span>
      <span className="muted mono">{c.missed ? 'Missed' : duration(c.durationSeconds)}</span>
    </li>
  );
}

function CallList({ calls, loading, me, dirName }: { calls: CallRecord[] | undefined; loading: boolean; me: string; dirName: (n: string) => string }) {
  if (!calls) return loading ? <Loading label="Loading calls" /> : <p className="muted">Call history isn’t available just now.</p>;
  if (calls.length === 0) return <Empty title="No calls this week" />;
  return (
    <ul className="call-list">
      {calls.map((c) => {
        const mine = (p: CallRecord['from']) => p.number === me;
        const other = mine(c.from) ? c.to : c.from;
        const inbound = !mine(c.from);
        // A Patch's call (1.12.18): "Patch ring: Ann → Dana", "Patch call: Ann → Dana · voicemail".
        const name = c.patch?.text || other.name || (other.number ? dirName(other.number) : '') || other.number || 'Unknown';
        return (
          <li key={c.id} className={c.missed ? 'missed' : ''}>
            <Icon name={inbound ? 'arrowIn' : 'arrowOut'} size={16} />
            <span className="cell-stack grow">
              <strong>{name}</strong>
              <small className="muted">
                {other.number && shownNumber(other.number) !== name ? `${shownNumber(other.number)} · ` : ''}
                {fullWhen(c.start)}
              </small>
            </span>
            <span className="muted mono">{c.missed ? 'Missed' : duration(c.durationSeconds)}</span>
          </li>
        );
      })}
    </ul>
  );
}

export function MyCalls() {
  const { tenant } = useTenant();
  const d = tenant.domain;
  const calls = useData(() => api.myCalls(d), [d], { topics: ['calls'] });
  const me = useData(() => api.me(d).catch(() => null), [d]);
  const dir = useData(() => api.directory(d).catch(() => undefined), [d]);
  const list = calls.data?.calls;
  const phone = usePhone();
  const [params, setParams] = useSearchParams();
  const calling = useCalls();
  // Live sessions (1.13.0): calls and Patches in one list.
  const sessions = useLiveSessions(d);
  // The phone app's Calls tab: recent calls, your voicemail (?show=voicemail), or the keypad
  // (?show=keypad) when calls in the app are on.
  if (phone) {
    const show = params.get('show') === 'voicemail' ? 'voicemail' : params.get('show') === 'keypad' && calling.on ? 'keypad' : 'recent';
    const choose = (to: 'recent' | 'voicemail' | 'keypad') => setParams(to === 'recent' ? {} : { show: to }, { replace: true });
    const voicemail = show === 'voicemail';
    return (
      <div className="phome pcalls">
        <PhonePageHead title="Calls" />
        <div className="pfeed-show" role="tablist" aria-label="Calls">
          <button type="button" role="tab" className="pfeed-chip" aria-selected={show === 'recent'} aria-pressed={show === 'recent'} onClick={() => choose('recent')}>
            Recent
          </button>
          <button type="button" role="tab" className="pfeed-chip" aria-selected={voicemail} aria-pressed={voicemail} onClick={() => choose('voicemail')}>
            Voicemail
          </button>
          {calling.on && (
            <button type="button" role="tab" className="pfeed-chip" aria-selected={show === 'keypad'} aria-pressed={show === 'keypad'} onClick={() => choose('keypad')}>
              Keypad
            </button>
          )}
        </div>
        {show === 'keypad' ? (
          <div className="pcalls-keypad">
            <DialPad />
          </div>
        ) : voicemail ? (
          <VoicemailBox domain={d} dir={dir.data} own />
        ) : (
          <>
            <p className="muted pcalls-note">{list ? `${plural(list.length, 'call')} in the last week · ${timesIn()}` : timesIn()}</p>
            {calls.error && !sessions ? (
              <LoadError error={calls.error} retry={calls.reload} />
            ) : sessions ? (
              <SessionHistory domain={d} calls={list} loading={calls.loading} me={me.data?.extension ?? ''} dirName={(n) => lookup(dir.data, n).name} />
            ) : (
              <CallList calls={list} loading={calls.loading} me={me.data?.extension ?? ''} dirName={(n) => lookup(dir.data, n).name} />
            )}
            {!sessions && <PatchHistory domain={d} />}
          </>
        )}
      </div>
    );
  }
  return (
    <>
      <PageHeader eyebrow={list ? `${plural(list.length, 'call')} in the last week · ${timesIn()}` : timesIn()} title="My calls" />
      <div className="content">
        <Card>
          <div className="card-body">
            {calls.error && !sessions ? (
              <LoadError error={calls.error} retry={calls.reload} />
            ) : sessions ? (
              <SessionHistory domain={d} calls={list} loading={calls.loading} me={me.data?.extension ?? ''} dirName={(n) => lookup(dir.data, n).name} />
            ) : (
              <CallList calls={list} loading={calls.loading} me={me.data?.extension ?? ''} dirName={(n) => lookup(dir.data, n).name} />
            )}
          </div>
        </Card>
        {/* Patches: quick calls in the app ("Patch with Sam · 12 min"); in the list above with Live sessions. */}
        {!sessions && <PatchHistory domain={d} />}
      </div>
    </>
  );
}
